import { didApiUrl } from '@/environment';
import { INSIGHTS_DATE_RANGE } from '@/pages/agents/pages/insights/types';
import { authenticatedAxios } from '@/services/auth';
import {
    GetConversationDetailsResponse,
    GetInteractionDetailsResponse,
    GetInteractionsAggregatesResponse,
    GetInteractionsResponse,
} from '@d-id/types';
import axios from 'axios';

export interface GetInteractionsAggregatesParams {
    dateRange: INSIGHTS_DATE_RANGE;
    from: Date;
    to: Date;
    group_by?: { kind: string; field?: string };
    additional_group_bys?: { kind: string; field?: string }[];
    filters?: { kind: string; values: string[] }[];
    variables?: string[];
    agent_ids?: string[];
    isPublic?: boolean;
    shareId?: string;
}

export const getInteractionsAggregates = async ({
    from,
    to,
    group_by,
    additional_group_bys,
    filters,
    variables,
    agent_ids,
    isPublic = false,
    shareId,
}: GetInteractionsAggregatesParams): Promise<GetInteractionsAggregatesResponse> => {
    const basePath = isPublic ? '/public/insights' : '/insights';
    const url = new URL(`${didApiUrl}${basePath}/interactions-aggregates`);
    url.searchParams.set('from', from.toISOString());
    url.searchParams.set('to', to.toISOString());

    if (isPublic && shareId) {
        url.searchParams.set('share_id', shareId);
    } else if (agent_ids?.length) {
        url.searchParams.set('agent_ids', agent_ids.join(','));
    }

    if (group_by) {
        url.searchParams.set('group_by', JSON.stringify(group_by));
    }
    if (additional_group_bys?.length) {
        url.searchParams.set('additional_group_bys', JSON.stringify(additional_group_bys));
    }
    if (filters?.length) {
        url.searchParams.set('filters', JSON.stringify(filters));
    }
    if (variables?.length) {
        url.searchParams.set('variables', variables.join(','));
    }

    const axiosInstance = isPublic ? axios : authenticatedAxios;
    const response = await axiosInstance?.get(url.toString());
    return response?.data;
};

export interface GetInteractionsMultiAggregatesParams {
    dateRange: INSIGHTS_DATE_RANGE;
    from: Date;
    to: Date;
    group_by_groups?: string[][];
    agent_ids?: string[];
    variables?: string[];
    isPublic?: boolean;
    shareId?: string;
}

export const getInteractionsMultiAggregates = async ({
    from,
    to,
    group_by_groups,
    agent_ids,
    variables,
    isPublic = false,
    shareId,
}: GetInteractionsMultiAggregatesParams): Promise<GetInteractionsAggregatesResponse> => {
    const basePath = isPublic ? '/public/insights' : '/insights';
    const url = new URL(`${didApiUrl}${basePath}/interactions-multi-aggregates`);
    url.searchParams.set('from', from.toISOString());
    url.searchParams.set('to', to.toISOString());

    if (isPublic && shareId) {
        url.searchParams.set('share_id', shareId);
    } else if (agent_ids?.length) {
        url.searchParams.set('agent_ids', agent_ids.join(','));
    }

    if (group_by_groups?.length) {
        const encodedGroupByGroups = encodeURIComponent(JSON.stringify(group_by_groups));
        url.searchParams.set('group_by_groups', encodedGroupByGroups);
    }

    if (variables?.length) {
        url.searchParams.set('variables', variables.join(','));
    }

    const axiosInstance = isPublic ? axios : authenticatedAxios;
    const response = await axiosInstance?.get(url.toString());
    return response?.data;
};

export interface GetInteractionsParams {
    dateRange: INSIGHTS_DATE_RANGE;
    from: Date;
    to: Date;
    agent_ids?: string[];
    filters?: { kind: string; values: string[] }[];
    isPublic?: boolean;
    shareId?: string;
}

export const getInteractions = async ({
    from,
    to,
    agent_ids,
    filters,
    isPublic = false,
    shareId,
}: GetInteractionsParams): Promise<GetInteractionsResponse> => {
    const basePath = isPublic ? '/public/insights' : '/insights';
    const url = new URL(`${didApiUrl}${basePath}/interactions`);
    url.searchParams.set('from', from.toISOString());
    url.searchParams.set('to', to.toISOString());

    if (isPublic && shareId) {
        url.searchParams.set('share_id', shareId);
    } else if (agent_ids?.length) {
        url.searchParams.set('agent_ids', agent_ids.join(','));
    }

    if (filters?.length) {
        const encodedFilters = encodeURIComponent(JSON.stringify(filters));
        url.searchParams.set('filters', encodedFilters);
    }

    const axiosInstance = isPublic ? axios : authenticatedAxios;
    const response = await axiosInstance?.get(url.toString());
    return response?.data;
};

export const getInteractionDetails = async (
    interactionId: string,
    isPublic = false,
    shareId?: string
): Promise<GetInteractionDetailsResponse> => {
    const basePath = isPublic ? '/public/insights' : '/insights';
    const url = `${didApiUrl}${basePath}/interactions/${interactionId}`;
    const urlWithParams = isPublic && shareId ? `${url}?share_id=${shareId}` : url;
    const axiosInstance = isPublic ? axios : authenticatedAxios;
    const response = await axiosInstance?.get(urlWithParams);
    return response?.data;
};

export const getConversationDetails = async (
    conversationId: string,
    isPublic = false,
    shareId?: string
): Promise<GetConversationDetailsResponse> => {
    const basePath = isPublic ? '/public/insights' : '/insights';
    const url = `${didApiUrl}${basePath}/conversations/${conversationId}`;
    const urlWithParams = isPublic && shareId ? `${url}?share_id=${shareId}` : url;
    const axiosInstance = isPublic ? axios : authenticatedAxios;
    const response = await axiosInstance?.get(urlWithParams);
    return response?.data;
};

export const getPublicInsights = async (agentId: string): Promise<{ shareId: string } | null> => {
    const response = await authenticatedAxios?.get(`${didApiUrl}/public/insights/${agentId}`);
    return response?.data;
};

export const createPublicInsights = async (agentId: string): Promise<{ id: string }> => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/public/insights/${agentId}`);
    return response?.data;
};

export const deletePublicInsights = async (agentId: string): Promise<void> => {
    await authenticatedAxios?.delete(`${didApiUrl}/public/insights/${agentId}`);
};

export const validatePublicInsightsShare = async (shareId: string): Promise<void> => {
    await axios.get(`${didApiUrl}/public/insights/share/${shareId}`);
};
